/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2017, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/


import React from "react"
import PropTypes from 'prop-types'
import {
  XYPlot,
  XAxis,
  YAxis,
  HorizontalGridLines,
  VerticalGridLines,
  LineSeries,
  LineMarkSeries,
  AreaSeries,
  Hint,
  Highlight
} from 'react-vis'

import {
  Button,
  CheckboxToggle,
  SvgIcon
} from '../'


export class Graph extends React.Component {
  static propTypes = {
    title: PropTypes.string,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    horizontalGridLines: PropTypes.bool,
    verticalGridLines: PropTypes.bool,
    xAxis: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.shape({
        title: PropTypes.string,
        tickValues: PropTypes.array,
        tickTotal: PropTypes.number,
        tickFormat: PropTypes.func,
        tickLabelAngle: PropTypes.number
      })
    ]),
    yAxis: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.shape({
        title: PropTypes.string,
        tickValues: PropTypes.array,
        tickTotal: PropTypes.number,
        tickFormat: PropTypes.func,
        tickLabelAngle: PropTypes.number
      })
    ]),
    lineSeries: PropTypes.arrayOf(PropTypes.shape({
      data: PropTypes.arrayOf(PropTypes.shape({
        x: PropTypes.number,
        y: PropTypes.number
      })),
      colour: PropTypes.string
    })),
    margin: PropTypes.shape({
      left: PropTypes.number,
      right: PropTypes.number,
      top: PropTypes.number,
      bottom: PropTypes.number
    }),
    hintFormat: PropTypes.func,
    allowZoom: PropTypes.bool,
    className: PropTypes.string,
    style: PropTypes.object
  }

  static defaultProps = {
    allowZoom: false
  }

  state = {
    value: null,
    zoom: null,
    zoomEnabled: false,
    startOnZeroY: true,
    lineMarkSeries: [
      {
        data: [{ x: -1, y: -1 }, ...this.props.lineMarkSeries[0].data]  // add initial point to start on zero on y axis
      }
    ]
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.startOnZeroY === false && this.state.startOnZeroY === true) {
      let lineMarkSeries = [ ...this.state.lineMarkSeries ]  // clones array
      lineMarkSeries[0].data.unshift({ x: -1, y: -1 })  // when graph starts on 0 for Y axis, coordinates gets added to the data point set for the desired visual effect
      this.setState({ lineMarkSeries })
    } else if (prevState.startOnZeroY === true && this.state.startOnZeroY === false) {
      let lineMarkSeries = [ ...this.state.lineMarkSeries ]
      lineMarkSeries[0].data.shift()  // when toggled not show graph starting on Y-axis 0, remove the first x-y set of coordinates
      this.setState({ lineMarkSeries })
    }
  }

  forgetValue = () => this.setState({ value: null })
  rememberValue = (value) => this.setState({ value })

  toggleZoom = () => (
    this.setState(
      prevState => (
        prevState.zoomEnabled
          ? { zoomEnabled: false, zoom: false }
          : { zoomEnabled: true }
      )
    )
  )

  toggleStartOnZeroY = () => this.setState(prevState => ({ startOnZeroY: !prevState.startOnZeroY }))

  render() {
    const lineSeries = this.props.lineSeries
      ? this.props.lineSeries.map((series, index) => (
          <LineSeries
            data={series.data}
            stroke={series.colour}
            opacity={series.opacity}
            colour={series.colour}
            getNull={dataPoint => dataPoint.y !== null}  // only draw datapoints where the y value is not equal to null
            key={`lineSeries_${index}`}
          />
        ))
      : null

    const lineMarkSeries = this.state.lineMarkSeries
      ? this.state.lineMarkSeries.map((series, index) => (
          <LineMarkSeries
            data={series.data}
            stroke={series.colour}
            opacity={series.opacity}
            color={series.color}
            fill={series.fill}
            size={series.size}
            strokeWidth={series.strokeWidth}
            getNull={dataPoint => dataPoint.y !== null && dataPoint.y > -1}
            onValueMouseOver={this.rememberValue}
            onValueMouseOut={this.forgetValue}
            key={`lineMarkseries_${index}`}
          />
        ))
      : null

    const areaSeries = this.props.areaSeries
      ? this.props.areaSeries.map((series, index) => (
          <AreaSeries
            data={series.data}
            stroke={series.color}
            opacity={series.opacity}
            color={series.color}
            key={`areaSeries_${index}`}
          />
        ))
      : null

    const { title, width, height, margin, horizontalGridLines, verticalGridLines, xAxis, yAxis,
      hintFormat, allowZoom, className, style } = this.props

    const { value, zoom, zoomEnabled, startOnZeroY } = this.state

    return (
      <div className={className} style={style}>
        <div className='slds-grid slds-m-bottom_medium'>
          <CheckboxToggle
            id='startOnZeroToggle'
            label={<b>Start graph on 0</b>}
            checked={startOnZeroY}
            onChange={this.toggleStartOnZeroY}
            className='slds-m-left_x-large slds-p-top_xx-small'
          />
          {allowZoom &&  // if zoom is allowed (set by prop), a toggle will activate the ability to drag a box on screen and zoom into
            <div className='slds-grid'>
              <CheckboxToggle
                id='zoomToggle'
                label={<b>Zoom</b>}
                checked={zoomEnabled}
                onChange={this.toggleZoom}
                className='slds-m-left_x-large slds-p-top_xx-small'
              />
              {zoomEnabled
                ? <div className={`slds-m-left_large${zoom ? '' : ' slds-p-top_xx-small'}`}>
                    Drag and select to zoom {zoom &&
                      <React.Fragment>
                        or <Button
                          type='reset'
                          onClick={() => this.setState({ zoom: null })}>
                          reset
                        </Button>
                      </React.Fragment>
                    }
                  </div>
                : <div className='slds-m-left_medium slds-p-top_xx-small slds-text-color_weak'>
                    <SvgIcon
                      variant='utility'
                      symbol='info'
                      size='x-small'
                      colour='light'
                      className='slds-p-right_x-small'
                    />
                    When zoom is disabled, hovering over data points will show their exact values.
                  </div>
              }
            </div>
          }
        </div>
        <div className='slds-align_absolute-center'>
          {title &&
            <div className="slds-text-title_caps slds-border_bottom slds-m-bottom_large">
              {title}
            </div>
          }
          <XYPlot
            animation={zoomEnabled}
            width={width}
            height={height}
            margin={margin}
            xDomain={
              zoom && [
                zoom.left,
                zoom.right
              ]
            }
            yDomain={
              zoom && [
                zoom.bottom,
                zoom.top
              ]
            }>
            {horizontalGridLines &&
              <HorizontalGridLines />
            }
            {verticalGridLines &&
              <VerticalGridLines />
            }
            {lineSeries}
            {areaSeries}
            {lineMarkSeries}
            {xAxis &&
              <XAxis
                title={xAxis.title}
                tickTotal={xAxis.tickTotal}
                tickFormat={xAxis.tickFormat}
                tickLabelAngle={xAxis.tickLabelAngle}
              />
            }
            {yAxis &&
              <YAxis
                title={yAxis.title}
                tickValues={yAxis.tickValues}
                tickTotal={yAxis.tickTotal}
                tickFormat={yAxis.tickFormat}
                tickLabelAngle={yAxis.tickLabelAngle}
              />
            }
            {!zoomEnabled && value   // if zoom is not enabled, hover over data points with show their exact value
              ? <Hint value={value}>
                  {hintFormat && hintFormat(value)}
                </Hint>
              : null
            }
            {zoomEnabled &&
              // highlight box drawn that becomes the boundary of zoom
              <Highlight
                onBrushEnd={area => this.setState({ zoom: area })}
                onDrag={area => {
                  this.setState({
                    zoom: {
                      bottom: zoom.bottom + (area.top - area.bottom),
                      left: zoom.left - (area.right - area.left),
                      right: zoom.right - (area.right - area.left),
                      top: zoom.top + (area.top - area.bottom)
                    }
                  })
                }}
              />
            }
          </XYPlot>
        </div>
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/graph/index.js